// Promise-based styled confirm + prompt (Phase 0, critique X2). Replaces window.confirm()
// and window.prompt() so high-stakes moments get a themed, accessible, focus-trapped dialog
// instead of the grey native browser box.
//
//   const confirm = useConfirm();
//   if (await confirm(msg)) { ...place order... }
//
//   const prompt = usePrompt();
//   const name = await prompt({ title: "Save mix as…", initial: "My mix 1" });
//
// confirm() accepts either the legacy multi-line message string (parsed into title/body/
// demo-live badge, so existing call sites are untouched) or an explicit options object.
import { createContext, useCallback, useContext, useRef, useState } from "react";
import { Modal } from "./Modal.jsx";
import { Button } from "./Button.jsx";

const ConfirmCtx = createContext(() => Promise.resolve(false));
const PromptCtx = createContext(() => Promise.resolve(null));
export const useConfirm = () => useContext(ConfirmCtx);
export const usePrompt = () => useContext(PromptCtx);

// Turn a legacy window.confirm() string into structured fields.
function parseMsg(msg) {
  const lines = String(msg).split("\n").map((s) => s.trim()).filter(Boolean);
  const title = lines[0] || "Confirm";
  const isLive = /\bLIVE\b|real money/i.test(msg);
  const isDemo = /DEMO|paper money/i.test(msg);
  const danger = /\b(close|sell|reduce|revoke|remove)\b/i.test(title);
  const badge = isLive
    ? { text: "LIVE — real money", cls: "confirm-badge--live" }
    : isDemo
    ? { text: "Trading 212 DEMO — paper money", cls: "confirm-badge--demo" }
    : null;
  const body = lines
    .slice(1)
    .filter((l) => !/Trading 212 (DEMO|LIVE)|paper money|real money/i.test(l))
    .join("\n");
  return { title, body, badge, danger };
}

export function ConfirmProvider({ children }) {
  // --- confirm ---
  const [c, setC] = useState(null);
  const cResolve = useRef(null);
  const confirm = useCallback((opts) => {
    // A same-tick double click can call a destructive action twice before React disables
    // its button. Never replace the resolver for an open dialog: doing so strands the first
    // caller forever and can let two mutations continue after one visible confirmation.
    if (cResolve.current) return Promise.resolve(false);
    const o = typeof opts === "string" ? parseMsg(opts) : opts || {};
    return new Promise((resolve) => {
      cResolve.current = resolve;
      setC(o);
    });
  }, []);
  const closeC = (val) => {
    setC(null);
    const r = cResolve.current;
    cResolve.current = null;
    r?.(val);
  };

  // --- prompt ---
  const [p, setP] = useState(null);
  const [pVal, setPVal] = useState("");
  const pResolve = useRef(null);
  const prompt = useCallback((opts) => {
    if (pResolve.current) return Promise.resolve(null);
    const o = typeof opts === "string" ? { title: opts } : opts || {};
    setPVal(o.initial || "");
    return new Promise((resolve) => {
      pResolve.current = resolve;
      setP(o);
    });
  }, []);
  const closeP = (val) => {
    setP(null);
    const r = pResolve.current;
    pResolve.current = null;
    r?.(val);
  };

  return (
    <ConfirmCtx.Provider value={confirm}>
      <PromptCtx.Provider value={prompt}>
        {children}

        <Modal
          open={!!c}
          onClose={() => closeC(false)}
          title={c?.title || "Confirm"}
          footer={
            <>
              <Button variant="ghost" onClick={() => closeC(false)}>
                {c?.cancelText || "Cancel"}
              </Button>
              <Button variant={c?.danger ? "danger" : "primary"} onClick={() => closeC(true)}>
                {c?.confirmText || "Confirm"}
              </Button>
            </>
          }
        >
          {c?.badge && <div className={"confirm-badge " + (c.badge.cls || "")}>{c.badge.text}</div>}
          {c?.body && <p className="confirm-body">{c.body}</p>}
        </Modal>

        <Modal
          open={!!p}
          onClose={() => closeP(null)}
          title={p?.title || "Enter a value"}
          footer={
            <>
              <Button variant="ghost" onClick={() => closeP(null)}>
                Cancel
              </Button>
              <Button onClick={() => closeP(pVal.trim() || null)} disabled={!pVal.trim()}>
                {p?.confirmText || "Save"}
              </Button>
            </>
          }
        >
          {p?.body && <p className="confirm-body">{p.body}</p>}
          <input
            className="ti confirm-input"
            aria-label={p?.title || "Enter a value"}
            value={pVal}
            placeholder={p?.placeholder || ""}
            onChange={(e) => setPVal(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && pVal.trim()) closeP(pVal.trim());
            }}
            autoFocus
          />
        </Modal>
      </PromptCtx.Provider>
    </ConfirmCtx.Provider>
  );
}
